import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
} from "@mui/material";
import { allCountries } from "country-telephone-data";
import { Country, State } from "country-state-city";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Loader from "../components/Loader";
import useIsMobile from "../Hooks/useIsMobile";
import useCurrencies from "../Hooks/useCurrencies";
import { useToast } from "../components/Toast";
import { getUserInfo } from "../store/rootReducer";
import apiService from "../Services/api.service";
import { UI, btnSx } from "../components/ActionDrawer";
import { PageHead } from "../components/DashTable";

// Standard 15-character Indian GSTIN format — same pattern the backend
// (zoho-books.service.js) validates against before sending to Zoho.
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

// Country code is entered separately, so the number itself should be plain
// digits only — no dashes/spaces/letters. 6-15 covers the shortest and
// longest real national numbers across the country list this form offers.
const PHONE_REGEX = /^\d{6,15}$/;
const digitsOnly = (value) => value.replace(/\D/g, "");

/* Section heading inside a wizard step. */
const Head = ({ children, sx }) => (
  <Box
    sx={{
      fontSize: 15,
      fontWeight: 500,
      color: UI.ink,
      margin: "0 0 14px",
      ...sx,
    }}
  >
    {children}
  </Box>
);

/* Sub heading above a field group or a toggle. */
const Label = ({ children, sx }) => (
  <Box
    sx={{
      fontSize: 12,
      fontWeight: 500,
      color: UI.ink2,
      letterSpacing: ".2px",
      marginBottom: "8px",
      ...sx,
    }}
  >
    {children}
  </Box>
);

/* Pill used for the Engage plan and tenure choices. */
const pillSx = (on) => ({
  textTransform: "none",
  boxShadow: "none",
  minWidth: 0,
  borderRadius: "14px",
  padding: "5px 14px",
  fontSize: 13,
  fontWeight: on ? 500 : 400,
  fontFamily: "inherit",
  border: "1px solid",
  borderColor: on ? UI.p : UI.line,
  bgcolor: on ? UI.p : UI.surface,
  color: on ? UI.surface : UI.ink2,
  "&:hover": {
    boxShadow: "none",
    bgcolor: on ? UI.pDark : UI.p50,
    borderColor: UI.p,
  },
});

const STEPS = [
  "Personal details",
  "Clinic details",
  "Quote acceptance",
  "Purchase agreement",
];

const SALUTATIONS = ["Dr.", "Mr.", "Ms.", "Mrs."];

const PRODUCTS = {
  Nova: { label: "scanO Nova Rover", price: 577500 },
  Air: { label: "scanO air", price: 314999 },
};

const BAG_PRICE = 8000;
const GST_RATES = {
  Air: 5,
  Nova: 5,
  engage: 18,
  bag: 18,
};

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

// Matches the geo codes actually stored on ScanoPlans.geoPricing (IN/AE/US) —
// note this is "AE", not the backend's own resolveSubscriptionRegion, which
// resolves a UAE clinic to "UAE" and would never match geoPricing there either.
const COUNTRY_TO_GEO = {
  in: "IN",
  india: "IN",
  ae: "AE",
  uae: "AE",
  "united arab emirates": "AE",
  us: "US",
  usa: "US",
  "united states": "US",
  "united states of america": "US",
};
const resolveGeo = (country) =>
  COUNTRY_TO_GEO[(country || "").trim().toLowerCase()] || "IN";


const calcEngageAmount = (plan, tenureMonths, discount, discountType, plansMap = {}) => {

  const selectedPlan = plansMap[plan];
  const months = Math.max(Number(tenureMonths) || 0, 0);

  let grossAmount = 0;
  if (months > 0) {
    // Exact years get annual pricing.
    // Custom partial periods get monthly pricing.
    if (months % 12 === 0) {
      grossAmount = (selectedPlan?.yearly || 0) * (months / 12);
    } else {
      grossAmount = (selectedPlan?.monthly || 0) * months;
    }
  }

  const preTaxBase = grossAmount / (1 + GST_RATES.engage / 100);
  const safeDiscount = Math.max(Number(discount) || 0, 0);
  let discountToSubtract = 0;
  if (discountType === "percent") {
    const safePercent = Math.min(safeDiscount, 100);
    discountToSubtract = (preTaxBase * safePercent) / 100;
  } else {
    discountToSubtract = Math.min(safeDiscount, preTaxBase);
  }
  const preTaxAfterDiscount = Math.max(preTaxBase - discountToSubtract, 0);
  const amount = round2(preTaxAfterDiscount * (1 + GST_RATES.engage / 100));
  const discountAmount = round2(grossAmount - amount);
  const gstAmount = round2(amount - (amount * 100) / (100 + GST_RATES.engage));

  return { grossAmount: round2(grossAmount), amount, discountAmount, gstAmount };
};

const rupee = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const computeFixedDiscountForTarget = (
  grossAmount,
  gstRatePercent,
  targetAmount,
) => {
  const preTaxBase = grossAmount / (1 + gstRatePercent / 100);
  const targetPreTax =
    Math.max(Number(targetAmount) || 0, 0) / (1 + gstRatePercent / 100);
  const discountToSubtract = Math.max(preTaxBase - targetPreTax, 0);
  return Math.round(Math.min(discountToSubtract, preTaxBase) * 100) / 100;
};

const StepRail = ({ step, isMobile }) => {
  if (isMobile) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
          overflowX: "auto",
        }}
      >
        {STEPS.map((label, index) => {
          const isDone = index < step;
          const isActive = index === step;
          return (
            <Box
              key={label}
              sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isActive || isDone ? UI.p : UI.surface,
                  border: "1px solid",
                  borderColor: isActive || isDone ? UI.p : UI.lineIn,
                  color: isActive || isDone ? UI.surface : UI.ink3,
                  fontSize: 11,
                  fontWeight: 500,
                }}
              >
                {index + 1}
              </Box>
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    width: 24,
                    height: "1px",
                    bgcolor: isDone ? UI.p : UI.line,
                    mx: 0.5,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minWidth: 260,
        flexShrink: 0,
        bgcolor: UI.bg2,
        border: "1px solid",
        borderColor: UI.line,
        borderRadius: "10px",
        padding: "26px 22px 8px",
        alignSelf: "flex-start",
      }}
    >
      {STEPS.map((label, index) => {
        const isDone = index < step;
        const isActive = index === step;
        return (
          <Box
            key={label}
            sx={{ display: "flex", gap: 2, pb: 4.5, position: "relative" }}
          >
            {index < STEPS.length - 1 && (
              <Box
                sx={{
                  position: "absolute",
                  left: 13,
                  top: 28,
                  bottom: 0,
                  width: "2px",
                  bgcolor: isDone ? UI.p : UI.line,
                }}
              />
            )}
            <Box
              sx={{
                width: 27,
                height: 27,
                borderRadius: "50%",
                flexShrink: 0,
                mt: "2px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 500,
                bgcolor: isActive || isDone ? UI.p : UI.surface,
                border: "1px solid",
                borderColor: isActive || isDone ? UI.p : UI.lineIn,
                color: isActive || isDone ? UI.surface : UI.ink3,
              }}
            >
              {isDone ? "✓" : index + 1}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  fontSize: 16,
                  fontWeight: isActive ? 500 : 400,
                  color: isActive || isDone ? UI.ink : UI.ink3,
                }}
              >
                {label}
              </Box>
              <Box sx={{ fontSize: 13, color: UI.ink3, marginTop: "3px" }}>
                {isDone ? "Done" : isActive ? "In progress" : ""}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const ToggleRow = ({ options, value, onChange }) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      border: "1px solid",
      borderColor: UI.line,
      borderRadius: "4px",
      overflow: "hidden",
      width: "fit-content",
      bgcolor: UI.surface,
    }}
  >
    {options.map((opt) => (
      <Box
        key={String(opt.value)}
        onClick={() => onChange(opt.value)}
        sx={{
          px: 2,
          py: "7px",
          cursor: "pointer",
          bgcolor: value === opt.value ? UI.p : "transparent",
          color: value === opt.value ? UI.surface : UI.ink2,
          fontWeight: value === opt.value ? 500 : 400,
          fontSize: 13,
          "&:hover":
            value === opt.value ? undefined : { bgcolor: UI.p50, color: UI.ink },
        }}
      >
        {opt.label}
      </Box>
    ))}
  </Box>
);

const FieldRow = ({ children }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
    {children}
  </Box>
);

const PersonalDetailsPanel = ({ personal, setPersonalField, ownerName }) => (
  <>
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 1,
        mb: 3,
      }}
    >
      <Box sx={{ fontSize: 15, fontWeight: 500, color: UI.ink }}>
        Personal details
      </Box>
      <Box sx={{ fontSize: 12, color: UI.ink3 }}>
        Owner: {ownerName || "—"}
      </Box>
    </Box>

    <FieldRow>
      <TextField
        select
        label="Salutation"
        value={personal.salutation}
        onChange={(e) => setPersonalField("salutation", e.target.value)}
        sx={{ width: 130, flexShrink: 0 }}
      >
        {SALUTATIONS.map((s) => (
          <MenuItem key={s} value={s}>
            {s}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        required
        label="First name"
        sx={{ flex: "1 1 160px" }}
        value={personal.firstName}
        onChange={(e) => setPersonalField("firstName", e.target.value)}
      />
      <TextField
        required
        label="Last name"
        sx={{ flex: "1 1 160px" }}
        value={personal.lastName}
        onChange={(e) => setPersonalField("lastName", e.target.value)}
      />
    </FieldRow>
    <FieldRow>
      <TextField
        select
        label="Country Code"
        sx={{ width: 120 }}
        value={personal.countryCode}
        onChange={(e) => setPersonalField("countryCode", e.target.value)}
      >
        {allCountries.map((country) => (
          <MenuItem
            key={country.dialCode + country.iso2}
            value={`+${country.dialCode}`}
          >
            {country.name} (+{country.dialCode})
          </MenuItem>
        ))}
      </TextField>

      <TextField
        required
        label="Contact number"
        sx={{ flex: 1 }}
        value={personal.contactNumber}
        onChange={(e) =>
          setPersonalField("contactNumber", digitsOnly(e.target.value))
        }
      />
    </FieldRow>

    <FieldRow>
      <TextField
        label="Alternate number"
        placeholder="Optional"
        sx={{ flex: 1 }}
        value={personal.alternateNumber}
        onChange={(e) =>
          setPersonalField("alternateNumber", digitsOnly(e.target.value))
        }
      />

      <TextField
        required
        label="Email"
        type="email"
        sx={{ flex: 1 }}
        value={personal.email}
        onChange={(e) => setPersonalField("email", e.target.value)}
      />
    </FieldRow>
  </>
);

const ClinicDetailsPanel = ({
  clinic,
  setClinicField,
  billTo,
  setBillToField,
  shipTo,
  setShipToField,
}) => {
  const countries = Country.getAllCountries();

  const states = clinic.country ? State.getStatesOfCountry(clinic.country) : [];

  return (
    <>
      <Head>Clinic details</Head>

      <TextField
        required
        label="Clinic name"
        fullWidth
        sx={{ mb: 2 }}
        value={clinic.name}
        onChange={(e) => setClinicField("name", e.target.value)}
      />

      <FieldRow>
        <TextField
          required
          label="Address line 1"
          sx={{ flex: 1 }}
          value={clinic.addressLine1}
          onChange={(e) => setClinicField("addressLine1", e.target.value)}
        />

        <TextField
          label="Address line 2"
          sx={{ flex: 1 }}
          value={clinic.addressLine2}
          onChange={(e) => setClinicField("addressLine2", e.target.value)}
        />
      </FieldRow>

      <FieldRow>
        <TextField
          required
          select
          label="Country Code"
          sx={{ width: 120 }}
          value={clinic.countryCode}
          onChange={(e) => setClinicField("countryCode", e.target.value)}
        >
          {allCountries.map((country) => (
            <MenuItem
              key={country.dialCode + country.iso2}
              value={`+${country.dialCode}`}
            >
              {country.name} (+{country.dialCode})
            </MenuItem>
          ))}
        </TextField>

        <TextField
          required
          label="Clinic Number"
          sx={{ flex: 1 }}
          value={clinic.mobile}
          onChange={(e) => setClinicField("mobile", digitsOnly(e.target.value))}
        />
      </FieldRow>

      {/* COUNTRY + STATE */}
      <FieldRow>
        <TextField
          required
          select
          label="Country"
          sx={{ flex: 1 }}
          value={clinic.country}
          onChange={(e) => {
            setClinicField("country", e.target.value);
            setClinicField("state", "");
            setClinicField("city", "");
          }}
        >
          {countries.map((country) => (
            <MenuItem key={country.isoCode} value={country.isoCode}>
              {country.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          required
          select
          label="State"
          sx={{ flex: 1 }}
          value={clinic.state}
          disabled={!clinic.country}
          onChange={(e) => {
            setClinicField("state", e.target.value);
            setClinicField("city", "");
          }}
        >
          {states.map((state) => (
            <MenuItem key={state.isoCode} value={state.isoCode}>
              {state.name}
            </MenuItem>
          ))}
        </TextField>
      </FieldRow>

      {/* CITY + PINCODE */}
      <FieldRow>
        <TextField
          required
          label="City "
          sx={{ flex: 1 }}
          value={clinic.city}
          onChange={(e) => setClinicField("city", e.target.value)}
        />
        <TextField
          required
          label="Pincode"
          sx={{ flex: 1 }}
          value={clinic.pincode}
          onChange={(e) => setClinicField("pincode", e.target.value)}
        />
      </FieldRow>

      <TextField
        label="Website"
        placeholder="Optional"
        fullWidth
        value={clinic.website}
        onChange={(e) => setClinicField("website", e.target.value)}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={clinic.hasGstin}
            onChange={(e) => {
              setClinicField("hasGstin", e.target.checked);
              if (!e.target.checked) setClinicField("gstin", "");
            }}
          />
        }
        label={
          <Box sx={{ fontSize: 13, color: UI.ink }}>Customer has a GSTIN</Box>
        }
        sx={{ mt: 1, mb: clinic.hasGstin ? 1 : 0 }}
      />

      {clinic.hasGstin && (
        <TextField
          label="GSTIN"
          fullWidth
          sx={{ mb: 2 }}
          value={clinic.gstin}
          onChange={(e) => setClinicField("gstin", e.target.value)}
        />
      )}

      {/* BILL TO */}
      <Head sx={{ marginTop: "28px" }}>Bill To</Head>

      <FormControlLabel
        control={
          <Checkbox
            checked={billTo.sameAsClinic}
            onChange={(e) => setBillToField("sameAsClinic", e.target.checked)}
          />
        }
        label={
          <Box sx={{ fontSize: 13, color: UI.ink }}>Same as clinic details</Box>
        }
        sx={{ mb: 1 }}
      />

      {billTo.sameAsClinic && (
        <Box
          sx={{
            padding: "10px 12px",
            marginBottom: "16px",
            bgcolor: UI.bg2,
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "6px",
          }}
        >
          <Box sx={{ fontSize: 11, color: UI.ink3, marginBottom: "2px" }}>
            Will use:
          </Box>
          <Box sx={{ fontSize: 13, color: UI.ink }}>
            {[
              clinic.name,
              clinic.addressLine1,
              clinic.addressLine2,
              clinic.city,
              clinic.state,
              clinic.pincode,
              clinic.country,
            ]
              .filter(Boolean)
              .join(", ") || (
              <Box component="span" sx={{ color: UI.red }}>
                Clinic details are incomplete — fill in Clinic
                name/address/city/state above first.
              </Box>
            )}
          </Box>
        </Box>
      )}

      {!billTo.sameAsClinic && (
        <>
          <TextField
            required
            label="Bill To Name"
            fullWidth
            sx={{ mb: 2 }}
            value={billTo.name}
            onChange={(e) => setBillToField("name", e.target.value)}
          />

          <FieldRow>
            <TextField
              required
              label="Address line 1"
              sx={{ flex: 1 }}
              value={billTo.addressLine1}
              onChange={(e) => setBillToField("addressLine1", e.target.value)}
            />
            <TextField
              label="Address line 2"
              sx={{ flex: 1 }}
              value={billTo.addressLine2}
              onChange={(e) => setBillToField("addressLine2", e.target.value)}
            />
          </FieldRow>

          <FieldRow>
            <TextField
              required
              select
              label="Country"
              sx={{ flex: 1 }}
              value={billTo.country}
              onChange={(e) => {
                setBillToField("country", e.target.value);
                setBillToField("state", "");
              }}
            >
              {Country.getAllCountries().map((country) => (
                <MenuItem key={country.isoCode} value={country.isoCode}>
                  {country.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              select
              label="State"
              sx={{ flex: 1 }}
              value={billTo.state}
              disabled={!billTo.country}
              onChange={(e) => setBillToField("state", e.target.value)}
            >
              {(billTo.country
                ? State.getStatesOfCountry(billTo.country)
                : []
              ).map((state) => (
                <MenuItem key={state.isoCode} value={state.isoCode}>
                  {state.name}
                </MenuItem>
              ))}
            </TextField>
          </FieldRow>

          <FieldRow>
            <TextField
              required
              label="City"
              sx={{ flex: 1 }}
              value={billTo.city}
              onChange={(e) => setBillToField("city", e.target.value)}
            />
            <TextField
              required
              label="Pincode"
              sx={{ flex: 1 }}
              value={billTo.pincode}
              onChange={(e) => setBillToField("pincode", e.target.value)}
            />
          </FieldRow>

          <FormControlLabel
            control={
              <Checkbox
                checked={billTo.hasGstin}
                onChange={(e) => {
                  setBillToField("hasGstin", e.target.checked);
                  if (!e.target.checked) setBillToField("gstin", "");
                }}
              />
            }
            label={
              <Box sx={{ fontSize: 13, color: UI.ink }}>Customer has a GSTIN</Box>
            }
            sx={{ mt: 1, mb: billTo.hasGstin ? 1 : 0 }}
          />

          {billTo.hasGstin && (
            <TextField
              label="GSTIN"
              fullWidth
              sx={{ mb: 2 }}
              value={billTo.gstin}
              onChange={(e) => setBillToField("gstin", e.target.value)}
            />
          )}
        </>
      )}

      {/* SHIP TO */}
      <Head sx={{ marginTop: "28px" }}>Ship To</Head>
      <FormControlLabel
        control={
          <Checkbox
            checked={shipTo.sameAsClinic}
            onChange={(e) => setShipToField("sameAsClinic", e.target.checked)}
          />
        }
        label={
          <Box sx={{ fontSize: 13, color: UI.ink }}>Same as clinic details</Box>
        }
        sx={{ mb: 1 }}
      />

      {shipTo.sameAsClinic && (
        <Box
          sx={{
            padding: "10px 12px",
            marginBottom: "16px",
            bgcolor: UI.bg2,
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "6px",
          }}
        >
          <Box sx={{ fontSize: 11, color: UI.ink3, marginBottom: "2px" }}>
            Will use:
          </Box>
          <Box sx={{ fontSize: 13, color: UI.ink }}>
            {[
              clinic.name,
              clinic.addressLine1,
              clinic.addressLine2,
              clinic.city,
              clinic.state,
              clinic.pincode,
              clinic.country,
            ]
              .filter(Boolean)
              .join(", ") || (
              <Box component="span" sx={{ color: UI.red }}>
                Clinic details are incomplete — fill in Clinic
                name/address/city/state above first.
              </Box>
            )}
          </Box>
        </Box>
      )}

      {!shipTo.sameAsClinic && (
        <>
          <TextField
            required
            label="Ship To Name"
            fullWidth
            sx={{ mb: 2 }}
            value={shipTo.name}
            onChange={(e) => setShipToField("name", e.target.value)}
          />

          <FieldRow>
            <TextField
              required
              label="Address line 1"
              sx={{ flex: 1 }}
              value={shipTo.addressLine1}
              onChange={(e) => setShipToField("addressLine1", e.target.value)}
            />
            <TextField
              label="Address line 2"
              sx={{ flex: 1 }}
              value={shipTo.addressLine2}
              onChange={(e) => setShipToField("addressLine2", e.target.value)}
            />
          </FieldRow>

          <FieldRow>
            <TextField
              required
              select
              label="Country"
              sx={{ flex: 1 }}
              value={shipTo.country}
              onChange={(e) => {
                setShipToField("country", e.target.value);
                setShipToField("state", "");
              }}
            >
              {Country.getAllCountries().map((country) => (
                <MenuItem key={country.isoCode} value={country.isoCode}>
                  {country.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              select
              label="State"
              sx={{ flex: 1 }}
              value={shipTo.state}
              disabled={!shipTo.country}
              onChange={(e) => setShipToField("state", e.target.value)}
            >
              {(shipTo.country
                ? State.getStatesOfCountry(shipTo.country)
                : []
              ).map((state) => (
                <MenuItem key={state.isoCode} value={state.isoCode}>
                  {state.name}
                </MenuItem>
              ))}
            </TextField>
          </FieldRow>

          <FieldRow>
            <TextField
              required
              label="City"
              sx={{ flex: 1 }}
              value={shipTo.city}
              onChange={(e) => setShipToField("city", e.target.value)}
            />
            <TextField
              required
              label="Pincode"
              sx={{ flex: 1 }}
              value={shipTo.pincode}
              onChange={(e) => setShipToField("pincode", e.target.value)}
            />
          </FieldRow>
        </>
      )}
    </>
  );
};

const QuoteAcceptancePanel = ({ quote, setQuoteField, pricing, enginePlans, enginePlansError }) => {
  const currencies = useCurrencies();
  const [productTargetAmount, setProductTargetAmount] = useState("");

  const applyProductTarget = () => {
    const gstRate = GST_RATES[quote.product] || 0;
    const discountValue = computeFixedDiscountForTarget(
      pricing.grossAmount,
      gstRate,
      productTargetAmount,
    );
    setQuoteField("discountType", "fixed");
    setQuoteField("discount", discountValue);
  };

  const addEngageAddOn = () => {
    // Default to a paid plan other than whichever is already the primary
    // one — whichever plans are actually paid comes from ScanoPlans, not a
    // fixed name list.
    const paidPlanKeys = Object.entries(enginePlans)
      .filter(([, plan]) => plan.yearly > 0)
      .map(([key]) => key);
    const defaultAddOnPlan =
      paidPlanKeys.find((key) => key !== quote.engagePlan) || paidPlanKeys[0] || "";

    setQuoteField("engageAddOns", (prev) => [
      ...(prev || []),
      {
        plan: defaultAddOnPlan,
        tenureMonths: 12,
        customTenure: false,
        discount: 0,
        discountType: "percent",
      },
    ]);
  };
  const removeEngageAddOn = (index) =>
    setQuoteField("engageAddOns", (prev) =>
      prev.filter((_, i) => i !== index),
    );
  const setEngageAddOnField = (index, name, value) =>
    setQuoteField("engageAddOns", (prev) =>
      prev.map((addOn, i) =>
        i === index ? { ...addOn, [name]: value } : addOn,
      ),
    );

  return (
    <>
      <Head>Quote acceptance</Head>
      {/* PAYMENT TYPE */}
      <Label>
        Payment type
      </Label>
      <Box sx={{ mb: 3 }}>
        <ToggleRow
          options={[
            { label: "Advance payment", value: "advance" },
            { label: "EMI", value: "emi" },
          ]}
          value={quote.paymentType}
          onChange={(value) => setQuoteField("paymentType", value)}
        />
        <Box sx={{ fontSize: 11, color: UI.ink3, marginTop: "8px" }}>
          Choose EMI if the doctor is financing this purchase through an
          installment loan — submitting the order then starts the EMI
          application flow. Choose Advance payment for a standard upfront or
          scheduled payment.
        </Box>
      </Box>
      {/* PRODUCT */}
      <TextField
        select
        label="Product"
        fullWidth
        value={quote.product}
        onChange={(e) => setQuoteField("product", e.target.value)}
        sx={{ mb: 2 }}
      >
        {Object.keys(PRODUCTS).map((key) => (
          <MenuItem key={key} value={key}>
            {PRODUCTS[key].label}
          </MenuItem>
        ))}
      </TextField>
      {/* CURRENCY */}
      <TextField
        select
        label="Currency"
        fullWidth
        value={quote.currency}
        onChange={(e) => setQuoteField("currency", e.target.value)}
        sx={{ mb: 2 }}
      >
        {currencies.map((code) => (
          <MenuItem key={code} value={code}>
            {code}
          </MenuItem>
        ))}
      </TextField>
      {/* BILLING STRUCTURE */}
      <Box
        sx={{
          mt: 2,
          mb: 3,
          border: "1px solid",
          borderColor: UI.line,
          borderRadius: "8px",
          overflow: "hidden",
          bgcolor: UI.surface,
        }}
      >
        <Box
          sx={{
            display: "grid",
            /* Matches the body row below so the columns line up. */
            gridTemplateColumns: "2fr 0.8fr 1fr 1.5fr 1.2fr",
            gap: 2,
            px: 2,
            py: "10px",
            bgcolor: UI.bg2,
            borderBottom: "1px solid",
            borderColor: UI.line,
            fontSize: 11,
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: ".5px",
            color: UI.ink3,
          }}
        >
          <Box>Item</Box>

          <Box>Quantity</Box>

          <Box>Rate</Box>

          <Box>Discount</Box>

          <Box sx={{ textAlign: "right" }}>Amount</Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2fr 0.8fr 1fr 1.5fr 1.2fr",
            gap: 2,
            alignItems: "center",
            px: 2,
            py: 2,
          }}
        >
          {/* ITEM */}
          <Box sx={{ fontSize: 13, color: UI.ink }}>
            {PRODUCTS[quote.product]?.label}
          </Box>
          {/* QUANTITY */}
          <TextField
            size="small"
            type="number"
            value={quote.units}
            onWheel={(e) => e.target.blur()}
            onFocus={(e) => e.target.select()}
            inputProps={{ min: 1 }}
            onChange={(e) =>
              setQuoteField("units", Math.max(1, Number(e.target.value) || 1))
            }
            sx={{ width: 75 }}
          />
          {/* RATE */}
          <Box sx={{ fontSize: 13, color: UI.ink }}>{rupee(pricing.rate)}</Box>

          {/* DISCOUNT */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                type="number"
                value={quote.discount}
                inputProps={{ min: 0 }}
                onWheel={(e) => e.target.blur()}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setQuoteField("discount", e.target.value)}
                sx={{ width: 90 }}
              />

              <TextField
                select
                size="small"
                value={quote.discountType}
                onChange={(e) => setQuoteField("discountType", e.target.value)}
                sx={{ width: 75 }}
              >
                <MenuItem value="percent">%</MenuItem>

                <MenuItem value="fixed">₹</MenuItem>
              </TextField>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                type="number"
                placeholder="Target amount"
                value={productTargetAmount}
                onWheel={(e) => e.target.blur()}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setProductTargetAmount(e.target.value)}
                sx={{ width: 130 }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={applyProductTarget}
                disabled={!productTargetAmount}
                sx={{
                  textTransform: "none",
                  boxShadow: "none",
                  fontSize: 13,
                  fontWeight: 500,
                  borderRadius: "4px",
                  minWidth: 0,
                  padding: "4px 12px",
                  border: "1px solid",
                  borderColor: UI.p100,
                  bgcolor: UI.surface,
                  color: UI.p,
                  "&:hover": { bgcolor: UI.p50, borderColor: UI.p },
                }}
              >
                Set
              </Button>
            </Box>
          </Box>

          {/* AMOUNT */}
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 500,
              textAlign: "right",
              color: UI.ink,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {rupee(pricing.productAmount)}
          </Box>
        </Box>
      </Box>

      {/* ENGAGE PLAN */}
      <Label>
        Engage subscription
      </Label>

      {Object.keys(enginePlans).length === 0 ? (
        <Box sx={{ fontSize: 12, color: UI.ink3, mb: 2 }}>
          {enginePlansError
            ? "Couldn't load plans from the plan model — refresh to retry."
            : "Loading plans…"}
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              mb: 1,
            }}
          >
            {Object.entries(enginePlans).map(([key, plan]) => (
              <Button
                key={key}
                variant={quote.engagePlan === key ? "contained" : "outlined"}
                onClick={() => setQuoteField("engagePlan", key)}
                sx={pillSx(quote.engagePlan === key)}
              >
                {plan.label}
              </Button>
            ))}
          </Box>

          {/* PLAN PRICE INFO — "Free" only when the model's own price for
          this plan is actually 0, never assumed from the plan's name/key. */}
          {(() => {
            const selected = enginePlans[quote.engagePlan];
            if (!selected) return null;
            return (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ fontSize: 12, fontWeight: 500, color: UI.ink2 }}>
                  {!selected.yearly
                    ? `${selected.label} — Free`
                    : `${selected.label} — ${rupee(selected.yearly)}/year • ${rupee(selected.monthly)}/month`}
                </Box>
              </Box>
            );
          })()}
        </>
      )}

      {/* ENGAGE DISCOUNT — hidden only for a genuinely free plan (model price
      is 0), not tied to any specific plan's name. */}
      {enginePlans[quote.engagePlan]?.yearly > 0 && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
          <Box sx={{ width: 140, fontSize: 12, color: UI.ink3 }}>
            Engage discount
          </Box>

          <TextField
            size="small"
            type="number"
            value={quote.engageDiscount}
            inputProps={{ min: 0 }}
            onWheel={(e) => e.target.blur()}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setQuoteField("engageDiscount", e.target.value)}
            sx={{ width: 90 }}
          />

          <TextField
            select
            size="small"
            value={quote.engageDiscountType}
            onChange={(e) =>
              setQuoteField("engageDiscountType", e.target.value)
            }
            sx={{ width: 75 }}
          >
            <MenuItem value="percent">%</MenuItem>

            <MenuItem value="fixed">₹</MenuItem>
          </TextField>
        </Box>
      )}

      {/* TENURE */}
      <Label>
        Engage subscription tenure
      </Label>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        {[12, 24, 36].map((months) => (
          <Button
            key={months}
            variant={
              !quote.customTenure && quote.tenureMonths === months
                ? "contained"
                : "outlined"
            }
            sx={pillSx(!quote.customTenure && quote.tenureMonths === months)}
            onClick={() => {
              setQuoteField("tenureMonths", months);

              setQuoteField("customTenure", false);
            }}
          >
            {months / 12} YR
          </Button>
        ))}

        <Button
          variant={quote.customTenure ? "contained" : "outlined"}
          sx={pillSx(Boolean(quote.customTenure))}
          onClick={() => {
            setQuoteField("customTenure", true);

            setQuoteField("tenureMonths", "");
          }}
        >
          OTHER
        </Button>
      </Box>

      {/* CUSTOM MONTHS */}
      {quote.customTenure && (
        <TextField
          label="Custom tenure (months)"
          type="number"
          fullWidth
          placeholder="Enter number of months"
          value={quote.tenureMonths}
          inputProps={{ min: 1 }}
          onChange={(e) => {
            const value = e.target.value;

            setQuoteField(
              "tenureMonths",
              value === "" ? "" : Math.max(1, Number(value)),
            );
          }}
          sx={{ mb: 3 }}
        />
      )}
      {enginePlans[quote.engagePlan]?.yearly > 0 && quote.tenureMonths && (
        <Box sx={{ fontSize: 12, color: UI.ink3, mb: 3 }}>
          Subscription amount for {quote.tenureMonths} months:{" "}
          <Box component="span" sx={{ fontWeight: 500, color: UI.ink }}>
            {rupee(pricing.engageAmount)}
          </Box>
        </Box>
      )}

      {/* ADDITIONAL ENGAGE PLANS — e.g. Plus for one kiosk and Pro for another,
          stacked on the same order */}
      {(quote.engageAddOns || []).map((addOn, index) => (
        <Box
          key={index}
          sx={{
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "6px",
            p: 1.5,
            mb: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {/* Add-on plans are whichever paid (non-zero) plans ScanoPlans
              actually has — a free plan isn't offered as an add-on, but which
              ones are paid is read from the model, not a fixed name list. */}
              {Object.entries(enginePlans)
                .filter(([, plan]) => plan.yearly > 0)
                .map(([key, plan]) => (
                  <Button
                    key={key}
                    variant={addOn.plan === key ? "contained" : "outlined"}
                    onClick={() => setEngageAddOnField(index, "plan", key)}
                    sx={pillSx(addOn.plan === key)}
                  >
                    {plan.label}
                  </Button>
                ))}
            </Box>

            <Button
              size="small"
              color="error"
              onClick={() => removeEngageAddOn(index)}
            >
              Remove
            </Button>
          </Box>

          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Checkbox
                size="small"
                checked={Boolean(addOn.sequence < 0)}
                onChange={(e) =>
                  setEngageAddOnField(
                    index,
                    "sequence",
                    e.target.checked ? -1 : null,
                  )
                }
              />
            }
            label={
              <Box sx={{ fontSize: 12, color: UI.ink3 }}>
                Activate this plan first, before the main{" "}
                {enginePlans[quote.engagePlan]?.label} plan
              </Box>
            }
          />

          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}>
            <Box sx={{ width: 140, fontSize: 12, color: UI.ink3 }}>
              Discount
            </Box>

            <TextField
              size="small"
              type="number"
              value={addOn.discount}
              inputProps={{ min: 0 }}
              onWheel={(e) => e.target.blur()}
              onFocus={(e) => e.target.select()}
              onChange={(e) =>
                setEngageAddOnField(index, "discount", e.target.value)
              }
              sx={{ width: 90 }}
            />

            <TextField
              select
              size="small"
              value={addOn.discountType}
              onChange={(e) =>
                setEngageAddOnField(index, "discountType", e.target.value)
              }
              sx={{ width: 75 }}
            >
              <MenuItem value="percent">%</MenuItem>
              <MenuItem value="fixed">₹</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
            {[12, 24, 36].map((months) => (
              <Button
                key={months}
                variant={
                  !addOn.customTenure && addOn.tenureMonths === months
                    ? "contained"
                    : "outlined"
                }
                sx={pillSx(!addOn.customTenure && addOn.tenureMonths === months)}
                onClick={() => {
                  setEngageAddOnField(index, "tenureMonths", months);
                  setEngageAddOnField(index, "customTenure", false);
                }}
              >
                {months / 12} YR
              </Button>
            ))}

            <Button
              variant={addOn.customTenure ? "contained" : "outlined"}
              sx={pillSx(Boolean(addOn.customTenure))}
              onClick={() => {
                setEngageAddOnField(index, "customTenure", true);
                setEngageAddOnField(index, "tenureMonths", "");
              }}
            >
              OTHER
            </Button>
          </Box>

          {addOn.customTenure && (
            <TextField
              label="Custom tenure (months)"
              type="number"
              fullWidth
              placeholder="Enter number of months"
              value={addOn.tenureMonths}
              inputProps={{ min: 1 }}
              onChange={(e) => {
                const value = e.target.value;
                setEngageAddOnField(
                  index,
                  "tenureMonths",
                  value === "" ? "" : Math.max(1, Number(value)),
                );
              }}
              sx={{ mb: 1 }}
            />
          )}

          {addOn.tenureMonths && (
            <Box sx={{ fontSize: 12, color: UI.ink3 }}>
              Subscription amount for {addOn.tenureMonths} months:{" "}
              <Box component="span" sx={{ fontWeight: 500, color: UI.ink }}>
                {rupee(pricing.engageAddOnAmounts[index]?.amount)}
              </Box>
            </Box>
          )}
        </Box>
      ))}

      <Button
        variant="outlined"
        size="small"
        onClick={addEngageAddOn}
        sx={{ mb: 3 }}
      >
        + Add another Engage plan
      </Button>

      {/* BAG */}
      <Label>
        Carry bag required
      </Label>

      <Box sx={{ mb: quote.bagRequired ? 2 : 3 }}>
        <ToggleRow
          options={[
            {
              label: `Yes, +${rupee(BAG_PRICE)} per unit`,
              value: true,
            },
            {
              label: "No",
              value: false,
            },
          ]}
          value={quote.bagRequired}
          onChange={(value) => setQuoteField("bagRequired", value)}
        />
      </Box>

      {/* BAG DISCOUNT */}
      {quote.bagRequired && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 3 }}>
          <Box sx={{ width: 140, fontSize: 12, color: UI.ink3 }}>
            Bag discount
          </Box>

          <TextField
            size="small"
            type="number"
            value={quote.bagDiscount}
            inputProps={{ min: 0 }}
            onWheel={(e) => e.target.blur()}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setQuoteField("bagDiscount", e.target.value)}
            sx={{ width: 90 }}
          />

          <TextField
            select
            size="small"
            value={quote.bagDiscountType}
            onChange={(e) => setQuoteField("bagDiscountType", e.target.value)}
            sx={{ width: 75 }}
          >
            <MenuItem value="percent">%</MenuItem>

            <MenuItem value="fixed">₹</MenuItem>
          </TextField>
        </Box>
      )}

      {/* SUMMARY */}
      <Box
        sx={{
          padding: "14px 16px",
          mb: 3,
          border: "1px solid",
          borderColor: UI.line,
          borderRadius: "8px",
          bgcolor: UI.surface,
        }}
      >
        <Box
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: UI.ink,
            marginBottom: "12px",
          }}
        >
          Quotation Summary
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box sx={{ fontSize: 13, color: UI.ink3 }}>Product subtotal</Box>

          <Box sx={{ fontSize: 13, color: UI.ink, fontVariantNumeric: "tabular-nums" }}>{rupee(pricing.grossAmount)}</Box>
        </Box>

        {pricing.discountAmount > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box sx={{ fontSize: 13, color: UI.ink3 }}>
              Discount (
              {quote.discountType === "percent"
                ? `${quote.discount}%`
                : rupee(quote.discount)}
              {quote.discountType === "fixed" ? ", on pre-tax base" : ""})
            </Box>

            <Box sx={{ fontSize: 13, color: UI.ink, fontVariantNumeric: "tabular-nums" }}>-{rupee(pricing.discountAmount)}</Box>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box sx={{ fontSize: 13, color: UI.ink3 }}>
            Engage subscription ({enginePlans[quote.engagePlan]?.label})
          </Box>

          <Box sx={{ fontSize: 13, color: UI.ink, fontVariantNumeric: "tabular-nums" }}>
            {pricing.engageGrossAmount === 0
              ? "Free"
              : rupee(pricing.engageGrossAmount)}
          </Box>
        </Box>

        {pricing.engageDiscountAmount > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box sx={{ fontSize: 13, color: UI.ink3 }}>
              Engage discount (
              {quote.engageDiscountType === "percent"
                ? `${quote.engageDiscount}%`
                : rupee(quote.engageDiscount)}
              )
            </Box>

            <Box sx={{ fontSize: 13, color: UI.ink, fontVariantNumeric: "tabular-nums" }}>-{rupee(pricing.engageDiscountAmount)}</Box>
          </Box>
        )}

        {(quote.engageAddOns || []).map((addOn, index) => (
          <React.Fragment key={index}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box sx={{ fontSize: 13, color: UI.ink3 }}>
                Engage subscription ({enginePlans[addOn.plan]?.label})
              </Box>

              <Box sx={{ fontSize: 13, color: UI.ink, fontVariantNumeric: "tabular-nums" }}>
                {rupee(pricing.engageAddOnAmounts[index]?.grossAmount)}
              </Box>
            </Box>

            {pricing.engageAddOnAmounts[index]?.discountAmount > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Box sx={{ fontSize: 13, color: UI.ink3 }}>
                  Engage discount (
                  {addOn.discountType === "percent"
                    ? `${addOn.discount}%`
                    : rupee(addOn.discount)}
                  )
                </Box>

                <Box sx={{ fontSize: 13, color: UI.ink, fontVariantNumeric: "tabular-nums" }}>
                  -{rupee(pricing.engageAddOnAmounts[index]?.discountAmount)}
                </Box>
              </Box>
            )}
          </React.Fragment>
        ))}

        {quote.bagRequired && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box sx={{ fontSize: 13, color: UI.ink3 }}>
              Carry bag × {pricing.units}
            </Box>

            <Box sx={{ fontSize: 13, color: UI.ink, fontVariantNumeric: "tabular-nums" }}>{rupee(pricing.bagGrossAmount)}</Box>
          </Box>
        )}

        {quote.bagRequired && pricing.bagDiscountAmount > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box sx={{ fontSize: 13, color: UI.ink3 }}>
              Bag discount (
              {quote.bagDiscountType === "percent"
                ? `${quote.bagDiscount}%`
                : rupee(quote.bagDiscount)}
              )
            </Box>

            <Box sx={{ fontSize: 13, color: UI.ink, fontVariantNumeric: "tabular-nums" }}>-{rupee(pricing.bagDiscountAmount)}</Box>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box sx={{ fontSize: 13, color: UI.ink3 }}>GST</Box>

          <Box sx={{ fontSize: 13, color: UI.ink, fontVariantNumeric: "tabular-nums" }}>{rupee(pricing.gstAmount)}</Box>
        </Box>

        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: UI.line,
            pt: 1.5,
            mt: 1,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ fontSize: 15, fontWeight: 500, color: UI.ink }}>
            Total
          </Box>

          <Box
            sx={{
              fontSize: 15,
              fontWeight: 500,
              color: UI.ink,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {rupee(pricing.total)}
          </Box>
        </Box>
      </Box>

      {/* EXPIRY DATE */}
      <Label>
        Quote expiry date (optional — defaults to 14 days from today)
      </Label>
      <TextField
        type="date"
        value={quote.expiryDate}
        onChange={(e) => setQuoteField("expiryDate", e.target.value)}
        sx={{ mb: 2 }}
        InputLabelProps={{ shrink: true }}
      />

      {/* PAYMENT TERMS */}
      <Label>
        Payment terms (finalized and mentioned in the quotation)
      </Label>

      <TextField
        required
        fullWidth
        multiline
        minRows={3}
        placeholder="Enter payment terms"
        value={quote.paymentTerm}
        onChange={(e) => setQuoteField("paymentTerm", e.target.value)}
        sx={{ mb: 2 }}
      />
    </>
  );
};
const PurchaseAgreementPanel = ({
  agreement,
  setAgreementField,
  personal,
  clinic,
  billTo,
}) => {
  const personalName =
    `${personal.firstName || ""} ${personal.lastName || ""}`.trim();

  const clinicAddress = [
    clinic.addressLine1,
    clinic.addressLine2,
    clinic.city,
    clinic.state,
    clinic.pincode,
  ]
    .filter(Boolean)
    .join(", ");

  useEffect(() => {
    if (!agreement.placeOfSupply) {
      const defaultState = billTo?.sameAsClinic ? clinic.state : billTo?.state;
      if (defaultState) {
        setAgreementField("placeOfSupply", defaultState);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Head>Purchase Agreement</Head>

      {/* PLACE OF SUPPLY */}
      <TextField
        required
        select
        label="Place of Supply"
        fullWidth
        sx={{ mb: 3 }}
        value={agreement.placeOfSupply}
        onChange={(e) => setAgreementField("placeOfSupply", e.target.value)}
      >
        {State.getStatesOfCountry(clinic.country || "IN").map((state) => (
          <MenuItem key={state.isoCode} value={state.isoCode}>
            {state.name}
          </MenuItem>
        ))}
      </TextField>

      {/* SIGNATORY */}
      <FieldRow>
        <TextField
          required
          label="Authorized Signatory Name"
          sx={{ flex: 1 }}
          value={agreement.signatoryName}
          onChange={(e) => setAgreementField("signatoryName", e.target.value)}
        />

        <TextField
          required
          label="Authorized Signatory Email ID"
          type="email"
          sx={{ flex: 1 }}
          value={agreement.signatoryEmail}
          onChange={(e) => setAgreementField("signatoryEmail", e.target.value)}
        />
      </FieldRow>

      <FormControlLabel
        control={
          <Checkbox
            checked={agreement.sameAsPersonal}
            onChange={(e) => {
              const checked = e.target.checked;

              setAgreementField("sameAsPersonal", checked);

              if (checked) {
                setAgreementField("signatoryName", personalName);
                setAgreementField("signatoryEmail", personal.email);
              } else {
                setAgreementField("signatoryName", "");
                setAgreementField("signatoryEmail", "");
              }
            }}
          />
        }
        label="Same as personal details"
      />

      {/* CLINIC NAME */}
      <TextField
        required
        label="Clinic Name"
        fullWidth
        sx={{ mt: 2 }}
        value={agreement.clinicName}
        onChange={(e) => setAgreementField("clinicName", e.target.value)}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={agreement.sameClinicName}
            onChange={(e) => {
              const checked = e.target.checked;

              setAgreementField("sameClinicName", checked);

              if (checked) {
                setAgreementField("clinicName", clinic.name || "");
              }
            }}
          />
        }
        label="Same as clinic details"
      />

      {/* CLINIC ADDRESS */}
      <TextField
        required
        label="Clinic Address"
        fullWidth
        multiline
        minRows={2}
        sx={{ mt: 2 }}
        value={agreement.clinicAddress}
        onChange={(e) => setAgreementField("clinicAddress", e.target.value)}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={agreement.sameClinicAddress}
            onChange={(e) => {
              const checked = e.target.checked;

              setAgreementField("sameClinicAddress", checked);

              if (checked) {
                setAgreementField("clinicAddress", clinicAddress);
              }
            }}
          />
        }
        label="Same as clinic details"
      />
    </>
  );
};
const AddOrder = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const userInfo = useSelector(getUserInfo);
  const isMobile = useIsMobile();
  const [step, setStep] = useState(0);
  const [Loading, isLoading] = useState(false);

  
  // No hardcoded fallback — starts empty and is only ever populated from
  // ScanoPlans (via apiService.getPlans). Until that fetch resolves (or if
  // it fails), plan buttons/prices simply aren't shown rather than falling
  // back to a stale hardcoded number.
  const [enginePlans, setEnginePlans] = useState({});
  const [enginePlansError, setEnginePlansError] = useState(false);

  const [personal, setPersonal] = useState({
    salutation: "Dr.",
    firstName: "",
    lastName: "",
    contactNumber: "",
    alternateNumber: "",
  });

  const [clinic, setClinic] = useState({
    name: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    country: "",
    countryCode: "",
    mobile: "",
    website: "",
    hasGstin: false,
    gstin: "",
  });

  // Refetches/recomputes whenever the clinic's country changes, so a plan's
  // price always reflects that clinic's actual region instead of always IN.
  useEffect(() => {
    let isMounted = true;
    const geo = resolveGeo(clinic.country);

    const loadEnginePlans = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await apiService.getPlans(token);
        const map = {};

        (res?.plans || []).forEach((plan) => {
          const key = plan.name?.toLowerCase();
          if (!key) return;

          const geoPrice =
            plan.geoPricing?.find((g) => g.geo === geo) || plan.geoPricing?.[0];
          const price = Number(geoPrice?.price || 0);
          const yearly = plan.billingCycle === "monthly" ? round2(price * 12) : price;

          map[key] = {
            label: plan.name,
            yearly,
            monthly: round2(yearly / 12),
          };
        });

        if (isMounted) {
          setEnginePlans(map);
          if (!Object.keys(map).length) setEnginePlansError(true);
        }
      } catch (err) {
        console.error("Failed to load Engage plans from the plan model:", err.message);
        if (isMounted) setEnginePlansError(true);
      }
    };

    loadEnginePlans();

    return () => {
      isMounted = false;
    };
  }, [clinic.country]);

  const [billTo, setBillTo] = useState({
    sameAsClinic: true,
    name: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    country: "",
    hasGstin: false,
    gstin: "",
  });

  const [shipTo, setShipTo] = useState({
    sameAsClinic: true,
    name: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    country: "",
  });
  const getDefaultExpiryDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().split("T")[0]; // YYYY-MM-DD for <input type="date">
  };

  const [quote, setQuote] = useState({
    product: "Air",
    currency: "INR",
    expiryDate: getDefaultExpiryDate(),

    units: 1,

    discount: 0,
    discountType: "percent",

    engagePlan: "starter",

    tenureMonths: 12,
    customTenure: false,

    engageDiscount: 0,
    engageDiscountType: "percent",

    engageAddOns: [],

    bagRequired: false,
    bagDiscount: 0,
    bagDiscountType: "percent",

    paymentType: "advance",
    paymentTerm: "",

    // ── AGREEMENT PDF KE LIYE — ADVANCE PATH (naya) ──
    advanceAmount: "",
    advanceSplitNote: "",
    hasSecondTranche: false,
    advanceSecondTrancheAmount: "",
    advanceSecondTrancheNote: "",
    balanceMode: "full_on_delivery",
    balanceAmount: "",
    pdcCount: "",
    pdcAmount: "",
    pdcDayOfMonth: 1,
    includePriceLockClause: false,

    // ── AGREEMENT PDF KE LIYE — EMI PATH (naya) ──
    emiUpfrontAmount: "",
    emiUpfrontMode: "NEFT",
  });

  const [agreement, setAgreement] = useState({
    signatoryName: "",
    signatoryEmail: "",
    sameAsPersonal: false,
    clinicName: "",
    sameClinicName: false,

    clinicAddress: "",
    sameClinicAddress: false,
    placeOfSupply: "",
  });

  const pricing = useMemo(() => {
    const product = PRODUCTS[quote.product];

    const units = Math.max(Number(quote.units) || 0, 0);

    // RATE OF ONE MACHINE
    const rate = Number(product?.price || 0);

    // QUANTITY × RATE
    const grossAmount = rate * units;

    // PRODUCT DISCOUNT
    const discount = Math.max(Number(quote.discount) || 0, 0);
    const productGstRate = GST_RATES[quote.product] || 0;
    const preTaxBase = grossAmount / (1 + productGstRate / 100);
    let discountToSubtract = 0;
    if (quote.discountType === "percent") {
      const safePercent = Math.min(discount, 100);
      discountToSubtract = (preTaxBase * safePercent) / 100;
    } else {
      discountToSubtract = Math.min(discount, preTaxBase);
    }
    const preTaxAfterDiscount = Math.max(preTaxBase - discountToSubtract, 0);
    const productAmount = round2(preTaxAfterDiscount * (1 + productGstRate / 100));
    const discountAmount = round2(grossAmount - productAmount);
    // ENGAGE SUBSCRIPTION (primary plan)
    const primaryEngage = calcEngageAmount(
      quote.engagePlan,
      quote.tenureMonths,
      quote.engageDiscount,
      quote.engageDiscountType,
      enginePlans,
    );
    const engageGrossAmount = primaryEngage.grossAmount;
    const engageAmount = primaryEngage.amount;
    const engageDiscountAmount = primaryEngage.discountAmount;

    // ADDITIONAL ENGAGE PLANS — e.g. Plus for one kiosk and Pro for another,
    // stacked on the same order. Each prices exactly like the primary plan.
    const engageAddOnAmounts = (quote.engageAddOns || []).map((addOn) =>
      calcEngageAmount(
        addOn.plan,
        addOn.tenureMonths,
        addOn.discount,
        addOn.discountType,
        enginePlans,
      ),
    );
    const addOnsAmount = engageAddOnAmounts.reduce(
      (sum, item) => sum + item.amount,
      0,
    );
    const addOnsGstAmount = engageAddOnAmounts.reduce(
      (sum, item) => sum + item.gstAmount,
      0,
    );
    // BAG
    const bagGrossAmount = quote.bagRequired ? BAG_PRICE * units : 0;

    const bagPreTaxBase = bagGrossAmount / (1 + GST_RATES.bag / 100);
    const bagDiscount = Math.max(Number(quote.bagDiscount) || 0, 0);
    let bagDiscountToSubtract = 0;
    if (quote.bagDiscountType === "percent") {
      const safePercent = Math.min(bagDiscount, 100);
      bagDiscountToSubtract = (bagPreTaxBase * safePercent) / 100;
    } else {
      bagDiscountToSubtract = Math.min(bagDiscount, bagPreTaxBase);
    }
    const bagPreTaxAfterDiscount = Math.max(
      bagPreTaxBase - bagDiscountToSubtract,
      0,
    );
    const bagAmount = round2(
      quote.bagRequired
        ? bagPreTaxAfterDiscount * (1 + GST_RATES.bag / 100)
        : 0,
    );
    const bagDiscountAmount = round2(bagGrossAmount - bagAmount);
    // GST (reverse-calculated — productAmount/engageAmount/bagAmount are treated as tax-inclusive)
    const productGstAmount = round2(
      productAmount - (productAmount * 100) / (100 + productGstRate),
    );

    const engageGstAmount = round2(
      engageAmount - (engageAmount * 100) / (100 + GST_RATES.engage),
    );

    const bagGstAmount = quote.bagRequired
      ? round2(bagAmount - (bagAmount * 100) / (100 + GST_RATES.bag))
      : 0;

    const gstAmount = round2(
      productGstAmount + engageGstAmount + bagGstAmount + addOnsGstAmount,
    );

    // FINAL TOTAL (inclusive — GST is baked into the amounts already, not added on top)
    const total = round2(productAmount + engageAmount + bagAmount + addOnsAmount);

    return {
      rate,
      units,
      grossAmount,
      discountAmount,
      productAmount,
      engageGrossAmount,
      engageDiscountAmount,
      engageAmount,
      engageAddOnAmounts,
      addOnsAmount,
      bagGrossAmount,
      bagDiscountAmount,
      bagAmount,
      productGstAmount,
      engageGstAmount,
      bagGstAmount,
      gstAmount,
      total,
    };
  }, [quote, enginePlans]);

  const setPersonalField = (name, value) =>
    setPersonal((prev) => ({ ...prev, [name]: value }));
  const setClinicField = (name, value) =>
    setClinic((prev) => ({ ...prev, [name]: value }));
  const setBillToField = (name, value) =>
    setBillTo((prev) => ({ ...prev, [name]: value }));
  const setShipToField = (name, value) =>
    setShipTo((prev) => ({ ...prev, [name]: value }));
  const setQuoteField = (name, value) =>
    setQuote((prev) => ({
      ...prev,
      [name]: typeof value === "function" ? value(prev[name]) : value,
    }));
  const setAgreementField = (name, value) =>
    setAgreement((prev) => ({ ...prev, [name]: value }));

  const validateStep = (currentStep) => {
    if (currentStep === 0) {
      if (!personal.salutation) return "Salutation is required";
      if (!personal.firstName.trim()) return "First name is required";
      if (!personal.lastName.trim()) return "Last name is required";
      if (!personal.countryCode) return "Country code is required";
      if (!personal.contactNumber.trim()) return "Contact number is required";
      if (!PHONE_REGEX.test(personal.contactNumber.trim()))
        return "Enter a valid contact number (digits only, no spaces or dashes)";
      if (
        personal.alternateNumber.trim() &&
        !PHONE_REGEX.test(personal.alternateNumber.trim())
      )
        return "Enter a valid alternate number (digits only, no spaces or dashes)";
      if (!personal.email.trim()) return "Email is required";
      return null;
    }

    if (currentStep === 1) {
      if (!clinic.name.trim()) return "Clinic name is required";
      if (!clinic.addressLine1.trim())
        return "Clinic address line 1 is required";
      if (!clinic.countryCode) return "Clinic country code is required";
      if (!clinic.mobile.trim()) return "Clinic number is required";
      if (!PHONE_REGEX.test(clinic.mobile.trim()))
        return "Enter a valid clinic number (digits only, no spaces or dashes)";
      if (!clinic.country) return "Clinic country is required";
      if (!clinic.state) return "Clinic state is required";
      if (!clinic.city.trim()) return "Clinic city is required";
      if (clinic.hasGstin && !GSTIN_REGEX.test(clinic.gstin.trim().toUpperCase()))
        return "Enter a valid 15-character GSTIN, or uncheck \"Customer has a GSTIN\"";

      if (!billTo.sameAsClinic) {
        if (!billTo.name.trim()) return "Bill To name is required";
        if (!billTo.addressLine1.trim())
          return "Bill To address line 1 is required";
        if (!billTo.country) return "Bill To country is required";
        if (!billTo.state) return "Bill To state is required";
        if (!billTo.city.trim()) return "Bill To city is required";
        if (billTo.hasGstin && !GSTIN_REGEX.test(billTo.gstin.trim().toUpperCase()))
          return "Enter a valid 15-character GSTIN, or uncheck \"Customer has a GSTIN\"";
      }

      if (!shipTo.sameAsClinic) {
        if (!shipTo.name.trim()) return "Ship To name is required";
        if (!shipTo.addressLine1.trim())
          return "Ship To address line 1 is required";
        if (!shipTo.country) return "Ship To country is required";
        if (!shipTo.state) return "Ship To state is required";
        if (!shipTo.city.trim()) return "Ship To city is required";
      }
      return null;
    }

    if (currentStep === 2) {
      if (!quote.paymentTerm.trim()) return "Payment terms are required";

      return null;
    }

    if (currentStep === 3) {
      if (!agreement.placeOfSupply) return "Place of Supply is required";
      if (!agreement.signatoryName.trim())
        return "Authorized Signatory Name is required";
      if (!agreement.signatoryEmail.trim())
        return "Authorized Signatory Email is required";
      if (!agreement.clinicName.trim()) return "Clinic Name is required";
      if (!agreement.clinicAddress.trim()) return "Clinic Address is required";
      return null;
    }

    return null;
  };

  const goNext = async () => {
    const error = validateStep(step);
    if (error) {
      toast.error(error);
      return;
    }

    // Same conflicts that would otherwise only fail at the very end (final
    // "Create order" submit) — checked right here instead, on this step's
    // own Continue, so it doesn't let you fill out the rest of the form
    // first only to find out at the end.
    if (step === 0 || step === 1) {
      const token = localStorage.getItem("token");
      try {
        isLoading(true);
        const params = {};
        if (step === 0) {
          params.mobile = personal.contactNumber.trim();
        }
        if (step === 1) {
          params.clinicName = clinic.name.trim();
          params.clinicAddress = `${clinic.addressLine1} ${clinic.addressLine2}`.trim();
        }
        const res = await apiService.checkOrderCreationConflicts(params, token);
        if (res.data?.mobileExists) {
          toast.error("A doctor with this mobile number already exists");
          return;
        }
        if (res.data?.clinicExists) {
          toast.error("A clinic with this name and address already exists");
          return;
        }
      } catch (checkError) {
        console.error("Error checking for duplicates:", checkError);
        // Don't block the user from continuing just because the check
        // itself failed — the fail-fast guard in createOrder is still
        // there as a backstop at final submit either way.
      } finally {
        isLoading(false);
      }
    }

    if (step === STEPS.length - 1) {
      handleSubmit();
      return;
    }
    setStep((prev) => prev + 1);
  };

  const goBack = () => setStep((prev) => Math.max(prev - 1, 0));

  const handleSubmit = async () => {
    try {
      isLoading(true);
      const doctorName =
        `${personal.salutation} ${personal.firstName} ${personal.lastName}`.trim();
      const resolvedBillTo = billTo.sameAsClinic
        ? {
            sameAsClinic: true,
            name: doctorName,
            address: [clinic.addressLine1, clinic.addressLine2]
              .filter(Boolean)
              .join(", "),
            addressLine1: clinic.addressLine1,
            addressLine2: clinic.addressLine2,
            city: clinic.city,
            state: clinic.state,
            pincode: clinic.pincode,
            country: clinic.country,
            gstin: clinic.gstin,
          }
        : { ...billTo, sameAsClinic: false, name: billTo.name || doctorName };

      const resolvedShipTo = shipTo.sameAsClinic
        ? {
            sameAsClinic: true,
            name: clinic.name,
            doctorName,
            address: [clinic.addressLine1, clinic.addressLine2]
              .filter(Boolean)
              .join(", "),
            addressLine1: clinic.addressLine1,
            addressLine2: clinic.addressLine2,
            city: clinic.city,
            state: clinic.state,
            pincode: clinic.pincode,
            country: clinic.country,
          }
        : { ...shipTo, sameAsClinic: false, name: shipTo.name || doctorName };

      const isEmi = quote.paymentType === "emi";

      const orderData = {
        personalDetails: personal,
        clinicDetails: clinic,
        billTo: resolvedBillTo,
        shipTo: resolvedShipTo,
        placeOfSupply: agreement.placeOfSupply,
        quote: {
          product: quote.product,
          currency: quote.currency,
          units: Number(quote.units),

          rate: pricing.rate,
          grossAmount: pricing.grossAmount,

          discount: Number(quote.discount || 0),
          discountType: quote.discountType,
          discountAmount: pricing.discountAmount,

          productAmount: pricing.productAmount,
          engagePlan: quote.engagePlan,
          tenureMonths: Number(quote.tenureMonths),
          engagePrice: pricing.engageAmount,
          engageGrossAmount: pricing.engageGrossAmount,

          engageDiscount: Number(quote.engageDiscount || 0),
          engageDiscountType: quote.engageDiscountType,
          engageDiscountAmount: pricing.engageDiscountAmount,

          engageAddOns: (quote.engageAddOns || []).map((addOn, index) => ({
            plan: addOn.plan,
            tenureMonths: Number(addOn.tenureMonths),
            price: pricing.engageAddOnAmounts[index].amount,
            grossAmount: pricing.engageAddOnAmounts[index].grossAmount,
            discount: Number(addOn.discount || 0),
            discountType: addOn.discountType,
            discountAmount: pricing.engageAddOnAmounts[index].discountAmount,
            sequence: addOn.sequence ?? null,
          })),

          bagRequired: quote.bagRequired,
          bagPrice: pricing.bagAmount,
          bagGrossAmount: pricing.bagGrossAmount,

          bagDiscount: Number(quote.bagDiscount || 0),
          bagDiscountType: quote.bagDiscountType,
          bagDiscountAmount: pricing.bagDiscountAmount,

          gstAmount: pricing.gstAmount,
          paymentType: quote.paymentType,
          paymentTerm: quote.paymentTerm,

          // ── AGREEMENT PDF KE LIYE (naya) ──
          // EMI: only upfront amount + mode matter, rest stay zero/blank so
          // agreement.service.js's EMI branch renders the single-line clause.
          // Advance: full structured breakdown, EMI fields stay blank.
          advanceAmount: isEmi
            ? Number(quote.emiUpfrontAmount || 0)
            : Number(quote.advanceAmount || 0),
          advanceSplitNote: isEmi
            ? quote.emiUpfrontMode || "NEFT"
            : quote.advanceSplitNote || "",
          advanceSecondTrancheAmount:
            !isEmi && quote.hasSecondTranche
              ? Number(quote.advanceSecondTrancheAmount || 0)
              : 0,
          advanceSecondTrancheNote:
            !isEmi && quote.hasSecondTranche
              ? quote.advanceSecondTrancheNote
              : "",
          balanceAmount:
            !isEmi && quote.balanceMode !== "nil"
              ? Number(quote.balanceAmount || 0)
              : 0,
          balanceMode: isEmi ? "nil" : quote.balanceMode,
          pdcCount:
            !isEmi && quote.balanceMode === "pdc"
              ? Number(quote.pdcCount || 0)
              : 0,
          pdcAmount:
            !isEmi && quote.balanceMode === "pdc"
              ? Number(quote.pdcAmount || 0)
              : 0,
          pdcDayOfMonth: Number(quote.pdcDayOfMonth || 1),
          includePriceLockClause: !isEmi && quote.includePriceLockClause,

          expiryDate: quote.expiryDate || undefined,
          totalAmount: pricing.total,
        },

        purchaseAgreement: agreement,
      };
      const token = localStorage.getItem("token");
      const res = await apiService.createOrder(orderData, token);

      isLoading(false);
      toast.success("Order created successfully..!");
      navigate(`/dashboard/orders/${res.data._id}`);
    } catch (error) {
      isLoading(false);
      const backendMessage = error.response?.data?.message;
      console.error(
        "Error creating order:",
        error.response ? error.response.data : error.message,
      );
      toast.error(backendMessage || "An error occurred while submitting data!");
    }
  };

  return (
    <Box
      sx={{
        padding: { xs: "18px 16px 50px", md: "24px 28px 60px" },
        maxWidth: 1240,
      }}
    >
      <PageHead
        title="New order"
        lede={`Step ${step + 1} of ${STEPS.length}: ${STEPS[step]}. Nothing is saved until you create the order.`}
      />

      <Box
        sx={{
          padding: isMobile ? "16px" : "22px 24px",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 2 : 4,
          bgcolor: UI.surface,
          border: "1px solid",
          borderColor: UI.line,
          borderRadius: "8px",
        }}
      >
        <StepRail step={step} isMobile={isMobile} />

        <Box sx={{ flex: 1, minWidth: 0, maxWidth: isMobile ? "100%" : 720 }}>
          {step === 0 && (
            <PersonalDetailsPanel
              personal={personal}
              setPersonalField={setPersonalField}
              ownerName={userInfo?.name}
            />
          )}
          {step === 1 && (
            <ClinicDetailsPanel
              clinic={clinic}
              setClinicField={setClinicField}
              billTo={billTo}
              setBillToField={setBillToField}
              shipTo={shipTo}
              setShipToField={setShipToField}
            />
          )}
          {step === 2 && (
            <QuoteAcceptancePanel
              quote={quote}
              setQuoteField={setQuoteField}
              pricing={pricing}
              enginePlans={enginePlans}
              enginePlansError={enginePlansError}
            />
          )}
          {step === 3 && (
            <PurchaseAgreementPanel
              agreement={agreement}
              setAgreementField={setAgreementField}
              personal={personal}
              clinic={clinic}
              billTo={billTo}
            />
          )}

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              marginTop: "30px",
              paddingTop: "18px",
              borderTop: "1px solid",
              borderTopColor: UI.line,
            }}
          >
            <Box sx={{ fontSize: 12, color: UI.ink3 }}>
              Step {step + 1} of {STEPS.length}
            </Box>
            <Box sx={{ display: "flex", gap: "8px" }}>
              {step > 0 && (
                <Box
                  component="button"
                  type="button"
                  onClick={goBack}
                  sx={btnSx("ghost")}
                >
                  Back
                </Box>
              )}
              <Box
                component="button"
                type="button"
                onClick={goNext}
                sx={btnSx("solid")}
              >
                {step === STEPS.length - 1 ? "Create order" : "Continue"}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {Loading && <Loader />}
    </Box>
  );
};

export default AddOrder;
